/**
 * CTL Info Tooltip
 * A small "info" icon that explains — in plain business language — what a visual
 * shows and how the number behind it is worked out.
 *
 * Every visual on the Close the Loop page should carry one of these so an end user
 * can understand the story the chart tells without needing to read the query behind it.
 */
import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { Info16Regular } from "@fluentui/react-icons";

interface CTLInfoTooltipProps {
    /** Short headline shown in bold at the top of the tooltip (usually the visual name) */
    title: string;
    /** Plain-language explanation of what the visual shows */
    description: string;
    /** Plain-language explanation of how the value is worked out */
    howItsCalculated?: string;
    /** Optional extra note, e.g. the time window or a caveat */
    note?: string;
    /** Icon size in pixels */
    size?: number;
    /** Icon colour */
    color?: string;
}

export const CTLInfoTooltip: React.FC<CTLInfoTooltipProps> = ({
    title,
    description,
    howItsCalculated,
    note,
    size = 15,
    color = "#5A5A72"
}) => (
    <Tooltip
        arrow
        placement="top"
        enterDelay={200}
        title={
            <Box sx={{ py: 0.5 }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 700, mb: 0.75 }}>{title}</Typography>
                <Typography sx={{ fontSize: "11.5px", lineHeight: 1.5, mb: howItsCalculated || note ? 0.75 : 0 }}>
                    {description}
                </Typography>
                {howItsCalculated && (
                    <Typography sx={{ fontSize: "11.5px", lineHeight: 1.5, mb: note ? 0.75 : 0 }}>
                        <b>How it&apos;s worked out:</b> {howItsCalculated}
                    </Typography>
                )}
                {note && (
                    <Typography sx={{ fontSize: "11px", lineHeight: 1.5, opacity: 0.85, fontStyle: "italic" }}>
                        {note}
                    </Typography>
                )}
            </Box>
        }
        componentsProps={{
            tooltip: {
                sx: {
                    maxWidth: 340,
                    backgroundColor: "#1A1A2E",
                    px: 1.5,
                    py: 1,
                    borderRadius: "8px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.22)"
                }
            },
            arrow: { sx: { color: "#1A1A2E" } }
        }}
    >
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                cursor: "help",
                flexShrink: 0,
                opacity: 0.7,
                transition: "opacity 0.2s ease",
                "&:hover": { opacity: 1 }
            }}
        >
            <Info16Regular style={{ width: size, height: size, color }} />
        </Box>
    </Tooltip>
);

export default CTLInfoTooltip;
